import { Card, CardHeader, Avatar, Box, Chip, Typography } from "@mui/material";
import React, { useContext } from "react";
import { Address } from "./Address";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import HomeIcon from "@mui/icons-material/Home";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BusinessIcon from "@mui/icons-material/Business";
import { useTheme } from "@mui/material/styles";
import { AddressContext } from "./AddressContext";

interface AddressCardProps {
  address: Address;
  size?: string;
}

export const AddressCard = (props: AddressCardProps) => {
  const { address, size = "large", ...rest } = props;
  const { selected, setSelected } = useContext(AddressContext);

  const theme = useTheme();

  const cardStyle = {
    margin: size === "small" ? "10px 10px 10px 0" : "15px",
    padding: size === "small" ? "5px" : "10px",
    "&:hover": {
      backgroundColor: theme.palette.grey["100"],
      transform: "scale(1.05)",
      cursor: "pointer",
    },
    "&.selected": {
      boxShadow:
        "0 4px 8px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.29)",
    },
    boxShadow:
      "0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.05)",
  };

  const sectionStyle = {
    margin: "5px 0 5px 15px",
    alignItems: "center",
    display: "flex",
  };

  const iconStyle = {
    marginRight: "10px",
    color: theme.palette.grey["700"],
    fontSize: size === "small" ? "16px" : "20px",
  };

  const textStyle = { fontSize: size === "small" ? "12px" : "15px" };

  const handleChange = () => {
    setSelected(address);
  };

  return (
    <Card
      sx={cardStyle}
      className={
        selected != null && selected.id === address.id ? "selected" : ""
      }
      {...rest}
      onClick={handleChange}
    >
      <CardHeader
        sx={{ padding: "12px" }}
        avatar={
          <Avatar
            sx={{
              bgcolor: "#f2f2f2",
            }}
          >
            <BusinessIcon
              color="primary"
              sx={{ fontSize: size === "small" ? "16px" : "20px" }}
            />
          </Avatar>
        }
        title={`${address?.first_name ?? ''} ${address?.last_name ?? ''}`}
        titleTypographyProps={
          size === "small"
            ? { fontSize: "14px", fontWeight: "500" }
            : { fontSize: "1.125rem", fontWeight: "600" }
        }
        subheader={address.company}
      />

      <Box display="flex" alignItems="center" sx={sectionStyle}>
        <HomeIcon sx={iconStyle} />
        <Box>
          <Typography
            sx={textStyle}
          >{`${address.street_address}, ${address.city} ${address.zip}`}</Typography>
          <Typography
            sx={textStyle}
          >{`${address.state ? address.state+',': ''} ${address.country}`}</Typography>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" sx={sectionStyle}>
        <LocalPhoneIcon sx={iconStyle} />
        <Box>
          <Typography sx={textStyle}>{address.phone}</Typography>
          <Typography sx={textStyle}>{address.mobile}</Typography>
        </Box>
      </Box>

      {address.email &&
      <Box display="flex" sx={sectionStyle}>
        <AlternateEmailIcon sx={iconStyle} />
        <Typography sx={textStyle}>{address.email}</Typography>
      </Box>
      }

      <Box display="flex" sx={{gap: "5px", ...sectionStyle}}>
        {address.default_billing ? (
          <Chip
            label="Default billing"
            variant="outlined"
            icon={<CreditCardIcon fontSize="small" />}
          />
        ) : (
          <></>
        )}
        {address.default_shipping ? (
          <Chip
            label="Default shipping"
            variant="outlined"
            icon={<LocalShippingIcon fontSize="small" />}
          />
        ) : (
          <></>
        )}
      </Box>
    </Card>
  );
};
